/**
 * Azure AD B2C sends people back to the app with an error when they leave a
 * step on purpose, e.g. Cancel on "Forgot your password?" (AADB2C90091) or
 * the password-reset link of the sign-in page (AADB2C90118). Those are not
 * real failures: we simply start the normal sign-in again.
 *
 * To make sure a broken B2C setup can never bounce the browser back and forth
 * for ever, automatic restarts are limited (MAX_RESTARTS within
 * RESTART_WINDOW_MS, kept in sessionStorage). After that the error screen with
 * a "Back to sign in" button is shown instead.
 */

export const RESTART_CODES = ["AADB2C90091", "AADB2C90118"] as const;
export const MAX_RESTARTS = 3;
export const RESTART_WINDOW_MS = 2 * 60 * 1000;
const STORAGE_KEY = "proc_b2c_signin_restarts";

type ErrorLike = {
  errorMessage?: unknown;
  errorCode?: unknown;
  message?: unknown;
} | null;

/** The B2C code (AADB2C9xxxx) of an MSAL error, if any. */
export const b2cErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== "object") {
    return null;
  }
  const e = error as ErrorLike;
  const text = [e?.errorMessage, e?.message, e?.errorCode]
    .filter((v) => typeof v === "string")
    .join(" ");
  const match = text.match(/AADB2C\d{5}/);
  return match ? match[0] : null;
};

/** True when the error only means "the person left a B2C step". */
export const isRestartError = (error: unknown): boolean => {
  const code = b2cErrorCode(error);
  return !!code && (RESTART_CODES as readonly string[]).includes(code);
};

/** Pure rule: may we restart again, given earlier restart times? */
export const canRestart = (earlier: number[], now: number): boolean =>
  earlier.filter((t) => now - t >= 0 && now - t < RESTART_WINDOW_MS).length <
  MAX_RESTARTS;

const readTimes = (): number[] => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list)
      ? list.filter((v): v is number => typeof v === "number")
      : [];
  } catch {
    return [];
  }
};

let pending = false;

/**
 * Called once per B2C "left a step" error. Returns true (and remembers the
 * attempt) when an automatic restart is allowed.
 */
export const claimRestart = (now: number = Date.now()): boolean => {
  const times = readTimes();
  if (!canRestart(times, now)) {
    pending = false;
    return false;
  }
  const kept = times.filter((t) => now - t < RESTART_WINDOW_MS);
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...kept, now]));
  } catch {
    // Without storage we still restart; B2C errors need a person's click,
    // so this cannot turn into a loop on its own.
  }
  pending = true;
  return true;
};

/** Is an automatic sign-in restart waiting to start (or started)? */
export const restartPending = (): boolean => pending;

export const clearRestartPending = (): void => {
  pending = false;
};
